import type { Invoice as InvoiceRecord } from "$global-providers/rxdb-provider/collections/invoice";
import type { BillingCurrency, PayerType } from "elephant-web/gql";

export type InvoicePayerReference =
  | { type: PayerType.Patient }
  | { type: PayerType.Insurer; name: string; id?: string };

export type InvoiceLineSource = Omit<
  InvoiceRecord["lineItems"][number],
  "quantity"
> & {
  quantity: number;
  payer: InvoicePayerReference;
};

export type InvoicePayerSource = Omit<
  InvoiceRecord["payers"][number],
  "balance" | "claimedLineItems"
>;

export type InvoiceTransaction = InvoiceRecord["transactions"][number] & {
  payerId?: string | null;
};

export type InvoicePhase =
  | "DRAFT"
  | "PENDING"
  | "FINALISED"
  | "REFUNDED"
  | "VOID";

export type InvoiceSource = Omit<
  InvoiceRecord,
  "balance" | "invoiceStatus" | "lineItems" | "payers" | "transactions"
> & {
  currency: BillingCurrency;
  phase: InvoicePhase;
  lineItems: InvoiceLineSource[];
  payers: InvoicePayerSource[];
  transactions: InvoiceTransaction[];
};

export type InvoiceBalance = {
  currency: BillingCurrency;
  total: number;
  due: number;
  paid: number;
  waived: number;
  exempt: number;
};

export type CalculatedInvoice = Omit<InvoiceRecord, "balance" | "payers"> & {
  balance: InvoiceBalance;
  payers: (InvoicePayerSource & {
    balance: InvoiceBalance;
    claimedLineItems: { lineItemId: string; amountInPence: number }[];
  })[];
};

export type InvoiceLinePricing = Pick<
  InvoiceLineSource,
  "cost" | "tariffId" | "priceListItemId" | "revenueDepartmentId" | "payer"
>;

export type InvoiceLineUpdate = Partial<
  Omit<InvoiceLineSource, "id" | keyof InvoiceLinePricing>
>;

export class InvoiceValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvoiceValidationError";
  }
}
